import { doc, writeBatch, Timestamp, getDoc, type DocumentData } from 'firebase/firestore';
import { db } from '../../config';
import { createResultHistory } from './history';
import { ResultHistoryActionType } from '../../../../types/resultHistory';

/** Firestore バッチ書き込み上限 */
const FIRESTORE_BATCH_LIMIT = 500;

export interface BulkUpdateAssigneeRequest {
  resultIds: string[];
  assigneeId: string;
  assigneeName: string;
  assigneeIconUrl: string | null;
  updatedBy: string;
  updatedByName: string;
}

export interface BulkUpdateAssigneeResponse {
  updatedCount: number;
}

/**
 * resultレコードの担当者を一括変更 #2642
 * 500件超は内部でバッチ分割する
 */
export const bulkUpdateAssignee = async ({
  resultIds,
  assigneeId,
  assigneeName,
  assigneeIconUrl,
  updatedBy,
  updatedByName,
}: BulkUpdateAssigneeRequest): Promise<BulkUpdateAssigneeResponse> => {
  if (resultIds.length === 0) {
    return { updatedCount: 0 };
  }

  const now = Timestamp.now();
  const updateData = {
    assignee_id: assigneeId,
    assignee_name: assigneeName,
    assignee_icon_url: assigneeIconUrl,
    assignee_set_at: now,
    assignee_set_by: updatedBy,
    updated_at: now,
    updated_by: updatedBy,
  };

  // 履歴記録用に変更前の値を取得
  const previousSnapshots = await Promise.all(
    resultIds.map(async id => {
      const snap = await getDoc(doc(db, 'results', id));
      return { id, data: snap.exists() ? (snap.data() as DocumentData) : null };
    }),
  );

  // バッチ書き込み（500件ずつ分割）
  let updatedCount = 0;
  for (let i = 0; i < resultIds.length; i += FIRESTORE_BATCH_LIMIT) {
    const chunk = resultIds.slice(i, i + FIRESTORE_BATCH_LIMIT);
    const batch = writeBatch(db);
    chunk.forEach(resultId => {
      batch.update(doc(db, 'results', resultId), updateData);
    });
    await batch.commit();
    updatedCount += chunk.length;
  }

  // 履歴記録（失敗してもメイン処理は止めない）
  await Promise.all(
    previousSnapshots.map(async ({ id, data }) => {
      if (!data) return;
      try {
        await createResultHistory({
          result_id: id,
          action_type: ResultHistoryActionType.EDIT,
          changed_fields: {
            assignee_id: assigneeId,
            assignee_name: assigneeName,
            assignee_icon_url: assigneeIconUrl,
          },
          previous_values: {
            assignee_id: data.assignee_id ?? null,
            assignee_name: data.assignee_name ?? null,
            assignee_icon_url: data.assignee_icon_url ?? null,
          },
          user_id: updatedBy,
          user_name: updatedByName,
        });
      } catch {
        // 履歴記録の失敗はメイン処理を止めない
      }
    }),
  );

  return { updatedCount };
};
